/** CSV import button component for rate plans. */
import { getCsrfToken } from "../../api/client";
import React, { useState, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from "@mui/material";
import {
  Upload as UploadIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";

/** Props for CSVImportButton. */
interface CSVImportButtonProps {
  /** Rate plan ID to import rates into */
  ratePlanId: string;
  /** Rate plan name for display */
  ratePlanName: string;
  /** Callback after successful import */
  onImportSuccess?: () => void;
  /** Disabled state */
  disabled?: boolean;
}

/** Parsed rate interface from CSV. */
interface ParsedRate {
  destination_prefix: string;
  rate: number;
  connect_fee: number;
  billing_increment: number;
}

/** B2: server-side preview diff (confirm=false response). */
interface PreviewRow {
  destination_prefix: string;
  rate: number;
  connect_fee: number;
  billing_increment: number;
  effective_start_date?: string | null;
  current?: { rate: number; connect_fee: number; billing_increment: number } | null;
  delta_pct?: number | null;
  scheduled?: boolean;
}

interface PreviewResponse {
  preview: boolean;
  added: PreviewRow[];
  changed: PreviewRow[];
  unchanged_count: number;
  total_rows: number;
  errors: string[];
}

/** CSV import button component. */
export default function CSVImportButton({
  ratePlanId,
  ratePlanName,
  onImportSuccess,
  disabled = false,
}: CSVImportButtonProps) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  // B2: two-phase import — preview diff first, apply only on confirmation
  const [preview, setPreview] = useState<PreviewResponse | null>(null);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    // Reset state
    setFile(null);
    setParsed([]);
    setPreview(null);
    setError("");
    setSuccess(false);
    const fileInput = document.getElementById("rate-csv-upload") as HTMLInputElement;
    if (fileInput) fileInput.value = "";
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      if (selected.name.endsWith(".csv")) {
        setFile(selected);
        setError("");
        parseCSV(selected);
      } else {
        setError("Only CSV files are supported");
      }
    }
  };

  const parseCSV = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split("\n").filter((l) => l.trim());
      if (lines.length === 0) {
        setError("CSV file is empty");
        return;
      }

      // Skip metadata headers (lines starting with #)
      const dataLines = lines.filter((l) => !l.trim().startsWith("#"));
      const headerLine = dataLines[0];
      const headers = headerLine.split(",").map((h) => h.trim().toLowerCase());

      // Check for required columns (case-insensitive)
      const hasDestination = headers.some((h) =>
        h === "destination_prefix" || h === "destination" || h === "dest" || h === "prefix"
      );
      const hasRate = headers.some((h) =>
        h === "rate" || h === "price" || h === "cost"
      );

      if (!hasDestination || !hasRate) {
        setError("CSV must have columns: destination_prefix, rate (optional: connect_fee, billing_increment)");
        return;
      }

      const rates: ParsedRate[] = [];
      // Find column indices
      const destIndex = headers.findIndex((h) =>
        h === "destination_prefix" || h === "destination" || h === "dest" || h === "prefix"
      );
      const rateIndex = headers.findIndex((h) =>
        h === "rate" || h === "price" || h === "cost"
      );
      const connectFeeIndex = headers.findIndex((h) => h === "connect_fee");
      const billingIncIndex = headers.findIndex((h) => h === "billing_increment");

      // Start from line after header
      for (let i = 1; i < dataLines.length; i++) {
        const cols = dataLines[i].split(",").map((c) => c.trim());
        if (cols.length >= 2 && cols[destIndex]) {
          const rate = parseFloat(cols[rateIndex]);
          if (!isNaN(rate)) {
            rates.push({
              destination_prefix: cols[destIndex],
              rate,
              connect_fee: connectFeeIndex >= 0 ? (parseFloat(cols[connectFeeIndex]) || 0) : 0,
              billing_increment: billingIncIndex >= 0 ? (parseInt(cols[billingIncIndex], 10) || 1) : 1,
            });
          }
        }
      }

      if (rates.length === 0) {
        setError("No valid rate data found in CSV");
        return;
      }

      setParsed(rates);
    };
    reader.readAsText(file);
  }, []);

  const postImport = async (confirm: boolean) => {
    const formData = new FormData();
    formData.append("file", file!);
    formData.append("tenant", "b1");

    const response = await fetch(
      `/api/v1/rate-plans/${ratePlanId}/import?confirm=${confirm}`,
      {
        method: "POST",
        headers: {
          // CSRF: required by backend middleware on state changes
          "X-CSRF-Token": await getCsrfToken(),
          // Don't set Content-Type for FormData - browser sets it with boundary
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        typeof errorData.detail === "string"
          ? errorData.detail
          : errorData.detail?.message || "Import failed"
      );
    }
    return response.json();
  };

  /** Phase 1: review — server-side diff, nothing written. */
  const handleSubmit = async () => {
    if (!file || parsed.length === 0) {
      setError("Please select a valid CSV file");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result: PreviewResponse = await postImport(false);
      setPreview(result);
    } catch (err: any) {
      setError(err.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  /** Phase 2: confirm — apply for real. */
  const handleConfirm = async () => {
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await postImport(true);
      setSuccess(true);
      setPreview(null);
      setParsed([]);
      setFile(null);
      const fileInput = document.getElementById("rate-csv-upload") as HTMLInputElement;
      if (fileInput) fileInput.value = "";

      if (onImportSuccess) {
        onImportSuccess();
      }

      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setParsed([]);
    setError("");
    const fileInput = document.getElementById("rate-csv-upload") as HTMLInputElement;
    if (fileInput) fileInput.value = "";
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={handleClickOpen}
        disabled={disabled}
      >
        Upload CSV
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Import Rates to {ratePlanName}</DialogTitle>

        <DialogContent>
          <Box mb={3}>
            <Paper
              sx={{
                p: 3,
                border: "2px dashed",
                borderColor: "divider",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
              onClick={() => document.getElementById("rate-csv-upload")?.click()}
            >
              <input
                id="rate-csv-upload"
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileChange}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: "action.active" }} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                {file ? file.name : "Click to upload CSV file"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Required columns: destination_prefix, rate (optional: connect_fee, billing_increment)
              </Typography>
            </Paper>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {typeof error === "string" ? error : JSON.stringify(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Rates imported successfully! Synced to CGRates.
            </Alert>
          )}

          {preview && (
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom>
                Changes about to be applied — review before confirming
              </Typography>
              <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                <Alert severity="success" sx={{ py: 0 }}>
                  {preview.added.length} new
                </Alert>
                <Alert severity="warning" sx={{ py: 0 }}>
                  {preview.changed.length} changed
                </Alert>
                <Alert severity="info" sx={{ py: 0 }}>
                  {preview.unchanged_count} unchanged
                </Alert>
              </Box>
              {(preview.added.length > 0 || preview.changed.length > 0) && (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Prefix</TableCell>
                        <TableCell>Change</TableCell>
                        <TableCell>Old → New ($/min)</TableCell>
                        <TableCell>Δ</TableCell>
                        <TableCell>Effective</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.added.map((r) => (
                        <TableRow key={`add-${r.destination_prefix}`}>
                          <TableCell>{r.destination_prefix}</TableCell>
                          <TableCell>New</TableCell>
                          <TableCell>— → ${r.rate.toFixed(4)}</TableCell>
                          <TableCell>—</TableCell>
                          <TableCell>{r.effective_start_date ? "scheduled" : "immediate"}</TableCell>
                        </TableRow>
                      ))}
                      {preview.changed.map((r) => (
                        <TableRow key={`chg-${r.destination_prefix}`}>
                          <TableCell>{r.destination_prefix}</TableCell>
                          <TableCell>Changed</TableCell>
                          <TableCell>
                            ${r.current?.rate.toFixed(4)} → ${r.rate.toFixed(4)}
                          </TableCell>
                          <TableCell>
                            {r.delta_pct != null ? `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct}%` : "—"}
                          </TableCell>
                          <TableCell>{r.scheduled ? "scheduled" : "immediate"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {parsed.length > 0 && !preview && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2">
                  Preview ({parsed.length} {parsed.length === 1 ? "row" : "rows"})
                </Typography>
                <Button size="small" onClick={handleClear} color="error">
                  Clear
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Destination Prefix</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Connect Fee</TableCell>
                      <TableCell>Increment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsed.slice(0, 10).map((rate, i) => (
                      <TableRow key={i}>
                        <TableCell>{rate.destination_prefix}</TableCell>
                        <TableCell>${rate.rate.toFixed(4)}</TableCell>
                        <TableCell>${rate.connect_fee.toFixed(2)}</TableCell>
                        <TableCell>{rate.billing_increment}s</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {parsed.length > 10 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  ... and {parsed.length - 10} more rows
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {preview && (
            <Button onClick={() => setPreview(null)} disabled={loading}>
              Back
            </Button>
          )}
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          {parsed.length > 0 && !preview && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
            >
              {loading ? "Reviewing..." : "Review Import"}
            </Button>
          )}
          {preview && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
            >
              {loading
                ? "Applying..."
                : `Confirm Import (${preview.added.length + preview.changed.length} changes)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
